import React, { useState } from 'react';
import { useAuth } from './AuthContext';

const Sidebar = ({ activeMenu, setActiveMenu, sidebarOpen, setSidebarOpen }) => {
  const { user } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState(['documents']); // Documents expanded by default

  // Configuration des permissions par rôle
  const getMenuItemsForRole = (userRole) => {
    const allMenuItems = [
      { id: 'dashboard', label: 'Tableau de bord', icon: '📊', roles: ['admin', 'manager', 'commercial', 'operateur_recouvrement', 'comex', 'user'] },
      { id: 'opportunities', label: 'Opportunités', icon: '🎯', roles: ['admin', 'manager', 'commercial'] },
      { id: 'pipeline', label: 'Pipeline KPIs', icon: '🚀', roles: ['admin', 'manager', 'commercial'] },
      { id: 'operations', label: 'Opérations', icon: '🚛', roles: ['admin', 'manager', 'commercial', 'operateur', 'operateur_recouvrement'] },
      { id: 'advanced-operations', label: 'Config Avancée', icon: '⚙️', roles: ['admin', 'manager'] },
      { 
        id: 'documents', 
        label: 'Documents', 
        icon: '📄', 
        roles: ['admin', 'manager', 'commercial', 'operateur_recouvrement'],
        subItems: [
          { id: 'documents', label: 'Tous les documents', icon: '📋' },
          { id: 'cgt-templates', label: 'Templates CGT', icon: '📝' },
          { id: 'payment-terms', label: 'Modalités paiement', icon: '💳' }
        ]
      },
      { id: 'transporteurs', label: 'Transporteurs', icon: '🚚', roles: ['admin', 'manager', 'operateur'] },
      { id: 'contacts', label: 'Annuaire Contacts', icon: '📞', roles: ['admin', 'manager', 'commercial', 'operateur_recouvrement'] },
      { id: 'clients', label: 'Sociétés', icon: '👥', roles: ['admin', 'manager', 'commercial', 'operateur_recouvrement'] },
      { id: 'export', label: 'Export & Rapports', icon: '📁', roles: ['admin', 'manager', 'operateur_recouvrement', 'comex'] },
      { id: 'settings', label: 'Paramètres', icon: '🔧', roles: ['admin', 'manager', 'commercial', 'user'] },
      { id: 'users', label: 'Utilisateurs', icon: '👤', roles: ['admin'] }
    ];

    // Filtrer les menus selon le rôle de l'utilisateur
    return allMenuItems.filter(item => item.roles.includes(userRole));
  };

  const toggleExpanded = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Obtenir les menus autorisés pour le rôle actuel
  const menuItems = user ? getMenuItemsForRole(user.role) : [];

  const handleMenuClick = (itemId) => {
    setActiveMenu(itemId);
    // Close sidebar on mobile after menu selection
    if (setSidebarOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className={`sidebar-responsive ${sidebarOpen ? 'sidebar-mobile-open' : ''}`}>
      <div className="p-6 border-b border-baobab-dark">
        <h1 className="text-xl font-bold text-white">Baobab Connect</h1>
        <p className="text-baobab-light text-sm mt-1">ERP / CRM Interne</p>
        {user && (
          <div className="mt-2">
            <p className="text-xs text-baobab-light">
              Connecté : {user.full_name}
            </p>
            <p className="text-xs text-baobab-light opacity-75">
              Rôle : {getRoleLabel(user.role)}
            </p>
          </div>
        )}
      </div>
      
      <nav className="p-4">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.id}>
              {item.subItems ? (
                // Menu avec sous-items
                <div>
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className={`sidebar-nav-item w-full flex justify-between items-center ${
                      activeMenu === item.id || activeMenu === 'cgt-templates' || activeMenu === 'payment-terms' ? 'active' : ''
                    }`}
                  >
                    <span className="flex items-center">
                      <span className="text-lg mr-3">{item.icon}</span>
                      {item.label}
                    </span>
                    <span className={`transition-transform duration-200 ${expandedMenus.includes(item.id) ? 'rotate-90' : ''}`}>
                      ▶
                    </span>
                  </button>
                  {/* Sous-menu */}
                  {expandedMenus.includes(item.id) && (
                    <ul className="ml-6 mt-1 space-y-1">
                      {item.subItems.map((subItem) => (
                        <li key={subItem.id}>
                          <button
                            onClick={() => handleMenuClick(subItem.id)}
                            className={`sidebar-nav-item w-full text-sm py-2 ${
                              activeMenu === subItem.id ? 'active' : ''
                            }`}
                          >
                            <span className="text-sm mr-2">{subItem.icon}</span>
                            {subItem.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ) : (
                // Menu simple
                <button
                  onClick={() => handleMenuClick(item.id)}
                  className={`sidebar-nav-item w-full ${
                    activeMenu === item.id ? 'active' : ''
                  }`}
                >
                  <span className="text-lg mr-3">{item.icon}</span>
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </ul>
        
        {/* Indicateur de restrictions pour certains rôles */}
        {user && ['operateur', 'operateur_recouvrement', 'comex'].includes(user.role) && (
          <div className="mt-6 p-3 bg-baobab-dark bg-opacity-20 rounded-lg">
            <p className="text-xs text-baobab-light">
              🔒 Accès personnalisé selon votre rôle
            </p>
            <p className="text-xs text-baobab-light opacity-75 mt-1">
              {getAccessDescription(user.role)}
            </p>
          </div>
        )}
      </nav>
    </div>
  );
};

// Fonction utilitaire pour les labels de rôles
const getRoleLabel = (role) => {
  const labels = {
    'admin': 'Administrateur',
    'manager': 'Manager',
    'commercial': 'Commercial',
    'operateur': 'Opérateur',
    'operateur_recouvrement': 'Opérateur de recouvrement',
    'comex': 'COMEX',
    'user': 'Utilisateur'
  };
  return labels[role] || role;
};

// Fonction utilitaire pour description des accès
const getAccessDescription = (role) => {
  const descriptions = {
    'operateur': 'Opérations (lecture seule) + Transporteurs',
    'operateur_recouvrement': 'Dashboard + 5 modules financiers + Sociétés + Contacts',
    'comex': 'Dashboard + Reports uniquement'
  };
  return descriptions[role] || '';
};

export default Sidebar;